import {
  Box,
  Button,
  TextField,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Divider,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import {useEffect, useState, useCallback} from 'react';
import message from 'src/components/Message';
import {
  CbwDeviceRegisterRequest,
  CbwDeviceRelayLinkCreateReq,
  CbwDeviceRelayLinkUpdateReq,
  CbwDeviceRelay,
} from 'src/apis/autoGenerate/types/cbwDevice';
import {
  cbwDeviceServiceRegister,
  cbwDeviceServiceGetRegistrationStatus,
  cbwDeviceServiceGet,
  cbwDeviceServiceCreateRelayLink,
  cbwDeviceServiceUpdateRelayLink,
  cbwDeviceServiceSync,
  intercomDeviceServiceFind,
} from 'src/apis/autoGenerate/services';
import {IntercomDevice} from 'src/apis/autoGenerate/types/intercomDevice';
import MacAddressInput from 'src/components/MacAddressInput';

interface CertificationRequestToken {
  token: string;
  expiresAt: string;
  expiresIn: number; // seconds remaining
  serialNumber: string;
  registrationId: string; // To track the registration
}

interface RelayLinkConfig {
  id?: string; // Exists if it's an existing link from backend
  intercomDevice: IntercomDevice | null;
  relayNumber: number;
  relayName: string;
  triggerDuration: number;
  isNew?: boolean; // True for newly added links not yet saved
  isEditing?: boolean; // True when currently being edited in the form
  isDeleted?: boolean; // True when marked for deletion
}

interface AddCbwDeviceModalProps {
  open: boolean;
  onClose: () => void;
  onDeviceAdded: () => void;
  orgId: string;
  // New props for edit mode
  mode?: 'add' | 'edit';
  editDeviceData?: {
    deviceId: string;
    serialNumber: string;
    existingRelayLinks?: {
      id: string;
      intercomDeviceId: string;
      intercomDeviceName?: string;
      relayNumber: number;
      relayName: string;
      triggerDurationMs: number;
    }[];
  };
}

export default function AddCbwDeviceModal({
  open,
  onClose,
  onDeviceAdded,
  orgId,
  mode = 'add',
  editDeviceData,
}: AddCbwDeviceModalProps) {
  const [serialNumber, setSerialNumber] = useState('');
  const [crt, setCrt] = useState<CertificationRequestToken | null>(null);
  const [crtGenerating, setCrtGenerating] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  // Update the state to store the internal device ID
  const [deviceId, setDeviceId] = useState<string>(''); // Internal device ID

  // Intercom devices state
  const [intercomDevices, setIntercomDevices] = useState<IntercomDevice[]>([]);
  const [loadingIntercomDevices, setLoadingIntercomDevices] = useState(false);

  // Multiple relay links management
  const [relayLinks, setRelayLinks] = useState<RelayLinkConfig[]>([]);
  const [savingLinks, setSavingLinks] = useState(false);

  // Form state for adding/editing a single relay link
  const [isAddingLink, setIsAddingLink] = useState(false);
  const [editingLinkIndex, setEditingLinkIndex] = useState<number | null>(null);
  const [formIntercomDevice, setFormIntercomDevice] = useState<IntercomDevice | null>(null);
  const [formRelayNumber, setFormRelayNumber] = useState<number>(1);
  const [formRelayName, setFormRelayName] = useState<string>('');
  const [formTriggerDuration, setFormTriggerDuration] = useState<number>(5000);

  // Available relays from CBW device
  const [availableRelays, setAvailableRelays] = useState<CbwDeviceRelay[]>([]);
  const [loadingDeviceDetails, setLoadingDeviceDetails] = useState(false);

  // Countdown timer for CRT expiration
  const [timeRemaining, setTimeRemaining] = useState<string>('');

  // Initialize form based on mode
  useEffect(() => {
    if (open) {
      console.log('Modal opened with mode:', mode, 'editDeviceData:', editDeviceData);

      if (mode === 'edit' && editDeviceData) {
        // Initialize edit mode
        setDeviceId(editDeviceData.deviceId);
        setSerialNumber(editDeviceData.serialNumber);
        setActiveStep(3); // Skip to intercom linking step

        console.log('Edit mode initialized with deviceId:', editDeviceData.deviceId);

        // Initialize relay links will happen after intercom devices are loaded
        // so we can map intercom device IDs to actual IntercomDevice objects
      } else {
        // Initialize add mode
        setActiveStep(0);
        setRelayLinks([]); // Start with empty relay links in add mode
      }
    }
  }, [open, mode, editDeviceData]);

  useEffect(() => {
    if (crt) {
      const interval = setInterval(() => {
        const now = Date.now();
        const expiresAt = new Date(crt.expiresAt).getTime();
        const remaining = Math.max(0, expiresAt - now);

        if (remaining <= 0) {
          setCrt(null);
          setTimeRemaining('');
          setActiveStep(0); // Reset to step 1
          clearInterval(interval);
          return;
        }

        const hours = Math.floor(remaining / (1000 * 60 * 60));
        const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((remaining % (1000 * 60)) / 1000);

        setTimeRemaining(
          `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds
            .toString()
            .padStart(2, '0')}`
        );
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [crt]);

  const loadDeviceDetails = useCallback(async () => {
    if (!deviceId) {
      console.warn('No deviceId provided to loadDeviceDetails');
      return;
    }

    setLoadingDeviceDetails(true);
    try {
      console.log('Fetching device details for deviceId:', deviceId);
      const response = await cbwDeviceServiceGet({
        deviceId: deviceId,
      });

      if (response.data.success && response.data.data) {
        const deviceData = response.data.data;
        console.log('Device details loaded:', deviceData);

        // Set available relays
        if (deviceData.relays && deviceData.relays.length > 0) {
          setAvailableRelays(deviceData.relays);
          console.log('Available relays:', deviceData.relays);
        } else {
          // Fallback to relay_count if relays array is not available
          const relayCount = deviceData.relay_count || 2;
          const fallbackRelays: CbwDeviceRelay[] = Array.from({length: relayCount}, (_, i) => ({
            number: i + 1,
            state: 0,
            name: `Relay ${i + 1}`,
          }));
          setAvailableRelays(fallbackRelays);
          console.log('Using fallback relays based on relay_count:', relayCount);
        }
      } else {
        console.warn('Failed to load device details');
        message.warning('Could not load device relay information');
      }
    } catch (error) {
      console.error('Failed to fetch device details:', error);
      message.error('Failed to load device details');
    } finally {
      setLoadingDeviceDetails(false);
    }
  }, [deviceId]);

  const loadIntercomDevices = useCallback(async () => {
    setLoadingIntercomDevices(true);
    try {
      const response = await intercomDeviceServiceFind(orgId);
      const devices = response.data?.intercomDevices || [];
      setIntercomDevices(devices);
      console.log('Loaded intercom devices:', devices.length);

      // In edit mode, initialize existing relay links
      if (mode === 'edit' && editDeviceData?.existingRelayLinks && devices.length > 0) {
        console.log('Initializing existing relay links:', editDeviceData.existingRelayLinks);

        const initializedLinks: RelayLinkConfig[] = editDeviceData.existingRelayLinks.map(link => {
          const intercomDevice = devices.find(device => device.id === link.intercomDeviceId);

          if (!intercomDevice) {
            console.warn('Intercom device not found for link:', link.intercomDeviceId);
          }

          return {
            id: link.id,
            intercomDevice: intercomDevice || null,
            relayNumber: link.relayNumber,
            relayName: link.relayName,
            triggerDuration: link.triggerDurationMs,
            isNew: false,
          };
        });

        setRelayLinks(initializedLinks);
        console.log('Initialized relay links:', initializedLinks);
      }
    } catch (error) {
      console.error('Failed to load intercom devices:', error);
      message.error('Failed to load intercom devices');
      setIntercomDevices([]);
    } finally {
      setLoadingIntercomDevices(false);
    }
  }, [orgId, mode, editDeviceData]);

  // Load device details when deviceId is available
  useEffect(() => {
    if (deviceId && open && (activeStep === 3 || mode === 'edit')) {
      loadDeviceDetails();
    }
  }, [deviceId, open, activeStep, mode, loadDeviceDetails]);

  // Load intercom devices when step 4 is reached or in edit mode
  useEffect(() => {
    if ((activeStep === 3 || mode === 'edit') && orgId && open) {
      loadIntercomDevices();
    }
  }, [activeStep, orgId, mode, open, loadIntercomDevices]);

  const generateCrt = async () => {
    if (!serialNumber) {
      message.error('Please enter a serial number');
      return;
    }

    setCrtGenerating(true);
    try {
      // Use the real API to register CBW device and generate CRT
      // Backend now uses user's organization context instead of explicit orgId
      const registerRequest: CbwDeviceRegisterRequest = {
        serialNumber: serialNumber,
        // Removed orgId - backend will use authenticated user's organization
      };

      // Fix: Wrap the request in the expected API request format
      const response = await cbwDeviceServiceRegister({
        cbwDeviceRegisterRequest: registerRequest,
      });
      const registerData = response.data.data;

      if (response.data.success && registerData) {
        const crtToken: CertificationRequestToken = {
          token: registerData.crtToken || '',
          expiresAt: registerData.expiresAt || '',
          expiresIn: registerData.expiresIn || 12 * 60 * 60, // 12 hours default
          serialNumber: registerData.serialNumber || serialNumber,
          registrationId: registerData.registrationId || '',
        };
        setCrt(crtToken);
        setCrtGenerating(false);
        message.success('Certification Request Token generated successfully');
      } else {
        throw new Error('Failed to generate CRT token');
      }
    } catch (error) {
      console.error('Failed to generate CRT:', error);
      message.error('Failed to generate CRT token');
      setCrtGenerating(false);
    }
  };

  const refreshDeviceStatus = async () => {
    if (!crt?.registrationId) {
      message.error('No registration ID available');
      return;
    }

    setRefreshing(true);
    try {
      // First, call the sync API to ensure latest device status
      let syncSuccess = false;
      try {
        const syncResponse = await cbwDeviceServiceSync();
        if (syncResponse.data.success) {
          syncSuccess = true;
          console.log('Device sync completed successfully');
          // Optional: Show sync results
          const syncData = syncResponse.data.data;
          if (syncData.deviceStatus.length > 0) {
            message.info('Device sync completed before checking status');
          }
        }
      } catch (syncError) {
        console.warn('Device sync failed, continuing with status check:', syncError);
        // Continue with status check even if sync fails
      }

      // Use the real API to check registration status
      const response = await cbwDeviceServiceGetRegistrationStatus({
        registrationId: crt.registrationId,
      });

      const registrationData = response.data.data;

      // Fix: Check for uppercase 'COMPLETED' status from the API
      if (
        registrationData?.registration_status?.toLowerCase() === 'completed' &&
        registrationData?.cbw_device_id
      ) {
        // Store the internal device ID
        const internalDeviceId = registrationData.entrio_controlbyweb_devices?.id;

        if (internalDeviceId) {
          setDeviceId(internalDeviceId); // Internal device ID for API calls
          setActiveStep(2); // Move to step 3 (Add Device)
          const successMessage = syncSuccess
            ? 'Device synced and is now online and ready to be added!'
            : 'Device is now online and ready to be added!';
          message.success(successMessage);
        } else {
          message.error('Device registration completed but device ID not found');
        }
      } else if (registrationData?.registration_status?.toLowerCase() === 'pending') {
        message.info(
          'Device registration is still pending. Please complete device configuration and try again.'
        );
      } else {
        message.info(
          'Device is not registered yet. Please complete device configuration and try again.'
        );
      }
      setRefreshing(false);
    } catch (error) {
      console.error('Failed to refresh device status:', error);
      message.error('Failed to check device status');
      setRefreshing(false);
    }
  };

  const addDevice = async () => {
    if (!serialNumber || !crt) {
      message.error('Please complete all previous steps');
      return;
    }

    setLoading(true);
    try {
      // Device should already be registered at this point, move to linking step
      setActiveStep(3); // Move to step 4 (Link with Intercom)
      setLoading(false);
      message.success('Device added successfully! You can now link it with an intercom device.');
    } catch (error) {
      message.error('Failed to add device');
      setLoading(false);
    }
  };

  // Helper functions for managing relay links
  const handleAddNewLink = () => {
    setIsAddingLink(true);
    setEditingLinkIndex(null);
    setFormIntercomDevice(null);
    setFormRelayNumber(1);
    setFormRelayName('');
    setFormTriggerDuration(5000);
  };

  const handleEditLink = (index: number) => {
    const link = relayLinks[index];
    setEditingLinkIndex(index);
    setIsAddingLink(false);
    setFormIntercomDevice(link.intercomDevice);
    setFormRelayNumber(link.relayNumber);
    setFormRelayName(link.relayName);
    setFormTriggerDuration(link.triggerDuration);
  };

  const handleDeleteLink = (index: number) => {
    const updatedLinks = [...relayLinks];
    const link = updatedLinks[index];

    if (link.isNew) {
      // If it's a new link not yet saved, just remove it from the list
      updatedLinks.splice(index, 1);
    } else {
      // If it's an existing link, mark it for deletion
      updatedLinks[index] = {...link, isDeleted: true};
    }

    setRelayLinks(updatedLinks);
    message.info('Relay link marked for deletion');
  };

  const handleSaveLink = () => {
    // Validation
    if (!formIntercomDevice) {
      message.error('Please select an intercom device');
      return;
    }
    if (!formRelayName.trim()) {
      message.error('Please enter a relay name');
      return;
    }

    if (editingLinkIndex !== null) {
      // Update existing link in the list
      const updatedLinks = [...relayLinks];
      updatedLinks[editingLinkIndex] = {
        ...updatedLinks[editingLinkIndex],
        intercomDevice: formIntercomDevice,
        relayNumber: formRelayNumber,
        relayName: formRelayName,
        triggerDuration: formTriggerDuration,
      };
      setRelayLinks(updatedLinks);
      message.success('Relay link updated');
    } else {
      // Add new link to the list
      const newLink: RelayLinkConfig = {
        intercomDevice: formIntercomDevice,
        relayNumber: formRelayNumber,
        relayName: formRelayName,
        triggerDuration: formTriggerDuration,
        isNew: true,
      };
      setRelayLinks([...relayLinks, newLink]);
      message.success('Relay link added');
    }

    // Reset form
    setIsAddingLink(false);
    setEditingLinkIndex(null);
    setFormIntercomDevice(null);
    setFormRelayNumber(1);
    setFormRelayName('');
    setFormTriggerDuration(5000);
  };

  const handleCancelForm = () => {
    setIsAddingLink(false);
    setEditingLinkIndex(null);
    setFormIntercomDevice(null);
    setFormRelayNumber(1);
    setFormRelayName('');
    setFormTriggerDuration(5000);
  };

  const handleSaveAllLinks = async () => {
    if (!deviceId) {
      message.error('Device ID is missing. Please try closing and reopening the dialog.');
      return;
    }

    setSavingLinks(true);
    try {
      const promises: Promise<any>[] = [];

      // Process each relay link
      for (const link of relayLinks) {
        if (link.isDeleted && link.id) {
          // Deactivate existing link by setting isActive to false
          console.log('Deactivating relay link:', link.id);
          if (!link.intercomDevice) continue;

          const deactivateRequest: CbwDeviceRelayLinkUpdateReq = {
            intercomDeviceId: link.intercomDevice.id,
            relayNumber: link.relayNumber,
            relayName: link.relayName,
            triggerDurationMs: link.triggerDuration,
            isActive: false,
          };

          promises.push(
            cbwDeviceServiceUpdateRelayLink({
              linkId: link.id,
              cbwDeviceRelayLinkUpdateReq: deactivateRequest,
            })
          );
        } else if (link.isNew) {
          // Create new link
          if (!link.intercomDevice) continue;

          const createRequest: CbwDeviceRelayLinkCreateReq = {
            intercomDeviceId: link.intercomDevice.id,
            relayNumber: link.relayNumber,
            relayName: link.relayName,
            triggerDurationMs: link.triggerDuration,
          };

          console.log('Creating relay link:', createRequest);
          promises.push(
            cbwDeviceServiceCreateRelayLink({
              deviceId: deviceId,
              cbwDeviceRelayLinkCreateReq: createRequest,
            })
          );
        } else if (link.id) {
          // Update existing link
          if (!link.intercomDevice) continue;

          const updateRequest: CbwDeviceRelayLinkUpdateReq = {
            intercomDeviceId: link.intercomDevice.id,
            relayNumber: link.relayNumber,
            relayName: link.relayName,
            triggerDurationMs: link.triggerDuration,
            isActive: true,
          };

          console.log('Updating relay link:', link.id, updateRequest);
          promises.push(
            cbwDeviceServiceUpdateRelayLink({
              linkId: link.id,
              cbwDeviceRelayLinkUpdateReq: updateRequest,
            })
          );
        }
      }

      // Execute all operations
      await Promise.all(promises);

      message.success('All relay links saved successfully!');
      onDeviceAdded();
      resetForm();
    } catch (error) {
      console.error('Failed to save relay links:', error);
      message.error('Failed to save some relay links. Please try again.');
    } finally {
      setSavingLinks(false);
    }
  };

  const skipLinking = () => {
    // Skip linking step and close modal
    onDeviceAdded();
    resetForm();
    const successMessage =
      mode === 'edit'
        ? 'Device settings updated without changing relay link'
        : 'Device added successfully without linking';
    message.success(successMessage);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    message.success('Copied to clipboard');
  };

  const resetForm = () => {
    setSerialNumber('');
    setCrt(null);
    setActiveStep(mode === 'edit' ? 3 : 0);
    setDeviceId(''); // Clear internal device ID
    setRelayLinks([]);
    setIsAddingLink(false);
    setEditingLinkIndex(null);
    setFormIntercomDevice(null);
    setFormRelayNumber(1);
    setFormRelayName('');
    setFormTriggerDuration(5000);
    onClose();
  };

  // Render relay links management UI
  const renderRelayLinksManagement = () => {
    const activeLinks = relayLinks.filter(link => !link.isDeleted);

    return (
      <Stack spacing={3}>
        <Alert severity="info">
          <Typography variant="body2">
            <strong>Manage Relay Links:</strong> Link multiple relays from this CBW device to
            different intercom devices. Each relay can control a different door or access point.
          </Typography>
        </Alert>

        {loadingIntercomDevices || loadingDeviceDetails ? (
          <Box sx={{display: 'flex', alignItems: 'center', gap: 2}}>
            <CircularProgress size={20} />
            <Typography>
              {loadingDeviceDetails ? 'Loading device details...' : 'Loading intercom devices...'}
            </Typography>
          </Box>
        ) : (
          <>
            {/* Existing Relay Links Table */}
            {activeLinks.length > 0 && (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <strong>Relay #</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Relay Name</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Intercom Device</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Duration (ms)</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Status</strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>Actions</strong>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {activeLinks.map((link, index) => (
                      <TableRow key={index}>
                        <TableCell>{link.relayNumber}</TableCell>
                        <TableCell>{link.relayName}</TableCell>
                        <TableCell>
                          {link.intercomDevice ? (
                            <Box>
                              <Typography variant="body2">{link.intercomDevice.name}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {link.intercomDevice.apartment?.name || 'No apartment'}
                              </Typography>
                            </Box>
                          ) : (
                            <Typography variant="body2" color="error">
                              Device not found
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>{link.triggerDuration}</TableCell>
                        <TableCell>
                          {link.isNew ? (
                            <Chip label="New" color="success" size="small" />
                          ) : (
                            <Chip label="Existing" size="small" />
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleEditLink(relayLinks.indexOf(link))}
                            disabled={isAddingLink || editingLinkIndex !== null}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteLink(relayLinks.indexOf(link))}
                            disabled={isAddingLink || editingLinkIndex !== null}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Add/Edit Form */}
            {(isAddingLink || editingLinkIndex !== null) && (
              <Paper sx={{p: 3, border: '2px solid', borderColor: 'primary.main'}}>
                <Stack spacing={2}>
                  <Typography variant="h6">
                    {editingLinkIndex !== null ? 'Edit Relay Link' : 'Add New Relay Link'}
                  </Typography>

                  <Autocomplete
                    fullWidth
                    options={intercomDevices}
                    getOptionLabel={option =>
                      `${option.name} (${option.apartment?.name || 'No apartment'})`
                    }
                    value={formIntercomDevice}
                    onChange={(event, newValue) => setFormIntercomDevice(newValue)}
                    renderInput={params => (
                      <TextField
                        {...params}
                        label="Select Intercom Device"
                        placeholder="Choose an intercom device to link"
                        required
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props}>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {option.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {option.apartment?.name || 'No apartment'} •{' '}
                            {option.building?.name || 'No building'}
                          </Typography>
                        </Box>
                      </li>
                    )}
                    noOptionsText="No intercom devices found"
                  />

                  <Box sx={{display: 'flex', gap: 2}}>
                    <FormControl sx={{minWidth: 200}}>
                      <InputLabel>Relay Number</InputLabel>
                      <Select
                        value={formRelayNumber}
                        label="Relay Number"
                        onChange={e => {
                          const selectedNum = Number(e.target.value);
                          setFormRelayNumber(selectedNum);
                          // Auto-fill relay name from available relays if empty
                          if (!formRelayName) {
                            const selectedRelay = availableRelays.find(
                              r => r.number === selectedNum
                            );
                            if (selectedRelay) {
                              setFormRelayName(selectedRelay.name || `Relay ${selectedNum}`);
                            }
                          }
                        }}
                        disabled={loadingDeviceDetails}
                      >
                        {availableRelays.length > 0
                          ? availableRelays.map(relay => {
                              const activeLinks = relayLinks.filter(link => !link.isDeleted);
                              const isUsed = activeLinks
                                .filter(
                                  (link, index) =>
                                    editingLinkIndex === null || index !== editingLinkIndex
                                )
                                .some(link => link.relayNumber === relay.number);

                              return (
                                <MenuItem key={relay.number} value={relay.number} disabled={isUsed}>
                                  {relay.name || `Relay ${relay.number}`}
                                  {isUsed && ' (Already linked)'}
                                </MenuItem>
                              );
                            })
                          : [1, 2, 3, 4, 5, 6, 7, 8].map(num => {
                              const activeLinks = relayLinks.filter(link => !link.isDeleted);
                              const isUsed = activeLinks
                                .filter(
                                  (link, index) =>
                                    editingLinkIndex === null || index !== editingLinkIndex
                                )
                                .some(link => link.relayNumber === num);

                              return (
                                <MenuItem key={num} value={num} disabled={isUsed}>
                                  Relay {num}
                                  {isUsed && ' (Already linked)'}
                                </MenuItem>
                              );
                            })}
                      </Select>
                    </FormControl>

                    <TextField
                      label="Relay Name"
                      value={formRelayName}
                      onChange={e => setFormRelayName(e.target.value)}
                      placeholder="e.g., Main Door"
                      sx={{flex: 1}}
                      required
                      helperText="Name for this relay link (can be different from device relay name)"
                    />
                  </Box>

                  <TextField
                    label="Trigger Duration (ms)"
                    type="number"
                    value={formTriggerDuration}
                    onChange={e => setFormTriggerDuration(Number(e.target.value))}
                    helperText="Duration in milliseconds (e.g., 5000 = 5 seconds)"
                    inputProps={{min: 1000, max: 30000, step: 1000}}
                    fullWidth
                  />

                  <Box sx={{display: 'flex', gap: 2, justifyContent: 'flex-end'}}>
                    <Button
                      variant="outlined"
                      startIcon={<CancelIcon />}
                      onClick={handleCancelForm}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<SaveIcon />}
                      onClick={handleSaveLink}
                      disabled={!formIntercomDevice || !formRelayName.trim()}
                    >
                      {editingLinkIndex !== null ? 'Update Link' : 'Add Link'}
                    </Button>
                  </Box>
                </Stack>
              </Paper>
            )}

            {/* Add New Link Button */}
            {!isAddingLink && editingLinkIndex === null && (
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleAddNewLink}
                sx={{alignSelf: 'flex-start'}}
              >
                Add Relay Link
              </Button>
            )}

            <Divider />

            {/* Action Buttons */}
            <Box sx={{display: 'flex', gap: 2, justifyContent: 'space-between'}}>
              <Button variant="outlined" onClick={resetForm}>
                Cancel
              </Button>

              <Box sx={{display: 'flex', gap: 2}}>
                {mode === 'add' && (
                  <Button variant="outlined" onClick={skipLinking}>
                    Skip Linking
                  </Button>
                )}
                <Button
                  variant="contained"
                  onClick={handleSaveAllLinks}
                  disabled={
                    savingLinks ||
                    activeLinks.length === 0 ||
                    isAddingLink ||
                    editingLinkIndex !== null
                  }
                  startIcon={savingLinks ? <CircularProgress size={20} /> : <SaveIcon />}
                >
                  {savingLinks
                    ? 'Saving...'
                    : `Save ${activeLinks.length} Link${activeLinks.length !== 1 ? 's' : ''}`}
                </Button>
              </Box>
            </Box>
          </>
        )}
      </Stack>
    );
  };

  // Render content based on mode
  const renderContent = () => {
    if (mode === 'edit') {
      // Edit mode: only show relay links management
      return renderRelayLinksManagement();
    }

    // Add mode: show full stepper
    return (
      <Stepper activeStep={activeStep} orientation="vertical">
        {/* Step 1: Generate CRT Token */}
        <Step>
          <StepLabel>
            <Typography variant="h6">Generate Certification Request Token</Typography>
          </StepLabel>
          <StepContent>
            <Stack spacing={3}>
              <Alert severity="info">
                Enter your device serial number (found on the device label) and generate a
                Certification Request Token (CRT).
              </Alert>

              <MacAddressInput value={serialNumber} onChange={setSerialNumber} />

              {!crt ? (
                <Button
                  variant="contained"
                  onClick={generateCrt}
                  disabled={!serialNumber || crtGenerating}
                  startIcon={crtGenerating ? <CircularProgress size={20} /> : undefined}
                  sx={{alignSelf: 'flex-start'}}
                >
                  {crtGenerating ? 'Generating...' : 'Generate CRT Token'}
                </Button>
              ) : (
                <Stack spacing={3}>
                  <Paper
                    sx={{
                      p: 2,
                      bgcolor: 'success.50',
                      border: '1px solid',
                      borderColor: 'success.200',
                    }}
                  >
                    <Stack spacing={2}>
                      <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                        <CheckCircleIcon color="success" />
                        <Typography variant="subtitle2" color="success.main">
                          Token Generated Successfully
                        </Typography>
                        <Typography variant="body2" color="error.main" sx={{ml: 'auto'}}>
                          Expires in: {timeRemaining}
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          p: 2,
                          bgcolor: 'white',
                          border: '1px solid',
                          borderColor: 'grey.300',
                          borderRadius: 1,
                          fontFamily: 'monospace',
                          fontSize: '0.875rem',
                          wordBreak: 'break-all',
                          position: 'relative',
                        }}
                      >
                        {crt.token}
                        <IconButton
                          size="small"
                          onClick={() => copyToClipboard(crt.token)}
                          sx={{position: 'absolute', top: 4, right: 4}}
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Stack>
                  </Paper>

                  {/* Add configuration guidance */}
                  <Alert severity="info" sx={{mt: 2}}>
                    <Typography variant="body2">
                      <strong>Next Steps - Configure Your Device:</strong>
                      <br />
                      <br />
                      1. <strong>Copy the token above</strong> and go to your Control by Web
                      Device's web UI
                      <br />
                      2. Navigate to{' '}
                      <strong>General Settings → Advanced Network → Remote Services</strong>
                      <br />
                      3. Select <strong>Certificate Request Method</strong> as{' '}
                      <strong>"Certificate Request Token"</strong>
                      <br />
                      4. <strong>Paste the token</strong> from Step 1 into the token field
                      <br />
                      5. <strong>Save the settings</strong> and wait for the device to register
                      <br />
                      <br />
                      <em>
                        Note: Registration may take 1-2 minutes. You can check the status in Step 2.
                      </em>
                    </Typography>
                  </Alert>

                  {/* Add a button to proceed to Step 2 */}
                  <Button
                    variant="outlined"
                    onClick={() => setActiveStep(1)}
                    sx={{alignSelf: 'flex-start'}}
                  >
                    Continue to Device Configuration
                  </Button>
                </Stack>
              )}
            </Stack>
          </StepContent>
        </Step>

        {/* Step 2: Configure Device */}
        <Step>
          <StepLabel>
            <Typography variant="h6">Configure Device</Typography>
          </StepLabel>
          <StepContent>
            <Stack spacing={3}>
              <Alert severity="warning">
                <Typography variant="body2">
                  <strong>Configure your device now:</strong>
                  <br />
                  1. Open your device's <strong>setup.html</strong> page in a browser
                  <br />
                  2. Navigate to <strong>Cloud Setup → Certificate Request Token</strong>
                  <br />
                  3. Paste the token from Step 1 and save settings
                  <br />
                  4. Wait for the device to register (may take 1-2 minutes)
                  <br />
                  5. Click "Check Device Status" below once configuration is complete
                </Typography>
              </Alert>

              <Button
                variant="outlined"
                onClick={refreshDeviceStatus}
                disabled={!crt || refreshing}
                startIcon={refreshing ? <CircularProgress size={20} /> : <RefreshIcon />}
                sx={{alignSelf: 'flex-start'}}
              >
                {refreshing ? 'Checking...' : 'Check Device Status'}
              </Button>
            </Stack>
          </StepContent>
        </Step>

        {/* Step 3: Add Device */}
        <Step>
          <StepLabel>
            <Typography variant="h6">Add Device to System</Typography>
          </StepLabel>
          <StepContent>
            <Stack spacing={3}>
              <Alert severity="success">
                <Typography variant="body2">
                  <strong>Device is online and ready!</strong>
                  <br />
                  Your device ({crt?.serialNumber}) has successfully registered and is now online.
                  Click "Add Device" to complete the integration.
                </Typography>
              </Alert>

              <Button
                variant="contained"
                onClick={addDevice}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <CheckCircleIcon />}
                sx={{alignSelf: 'flex-start'}}
              >
                {loading ? 'Adding Device...' : 'Add Device'}
              </Button>
            </Stack>
          </StepContent>
        </Step>

        {/* Step 4: Link with Intercom Devices */}
        <Step>
          <StepLabel>
            <Typography variant="h6">Link Relays with Intercom Devices (Optional)</Typography>
          </StepLabel>
          <StepContent>{renderRelayLinksManagement()}</StepContent>
        </Step>
      </Stepper>
    );
  };

  const getDialogTitle = () => {
    return mode === 'edit' ? 'Manage CBW Device Relay Links' : 'Add ControlbyWebDevice Integration';
  };

  return (
    <Dialog
      open={open}
      onClose={() => {}} // Prevent closing by clicking outside
      disableEscapeKeyDown // Prevent closing with Escape key
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle>{getDialogTitle()}</DialogTitle>
      <DialogContent>
        <Box sx={{mt: 2}}>{renderContent()}</Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={resetForm}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
}
